/**
 * Email Audit Trail Component
 * Tracks and displays all emails sent by the system
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ExpandMore as ExpandIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  Replay as ResendIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { apiGet, apiPost } from '../../utils/apiClient';
import SortableHeaderCell from '../common/SortableHeaderCell';
import useTableSort from '../../hooks/useTableSort';
import { useNotification } from '../../contexts/NotificationContext';

function EmailAuditTrail() {
  const notify = useNotification();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const sort = useTableSort({ onSortChange: () => setPage(0) });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterRecipient, setFilterRecipient] = useState('');

  useEffect(() => {
    loadEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadEmails is also called from filter/resend handlers
  }, [page, rowsPerPage, sort.apiParams]);

  const loadEmails = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...(filterStatus !== 'all' && { status: filterStatus }),
        ...(filterType !== 'all' && { notification_type: filterType }),
        ...(filterDateFrom && { date_from: filterDateFrom }),
        ...(filterDateTo && { date_to: filterDateTo }),
        ...(filterRecipient && { recipient: filterRecipient }),
        ...sort.apiParams,
      });

      // Use the new email logs API endpoint
      const response = await apiGet(`/email/logs?${params}`);
      if (response.success && response.data) {
        setEmails(response.data);
        setTotalCount(response.pagination?.total || response.data.length);
      } else {
        // No data available
        setEmails([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Error loading emails:', err);
      // Show empty state on error
      setEmails([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async (emailId) => {
    try {
      setResending(emailId);
      const response = await apiPost(`/email/resend/${emailId}`);
      if (response.success) {
        notify.success('Email resent successfully');
        // Reload the list to show updated status
        loadEmails();
      } else {
        throw new Error(response.message || 'Failed to resend email');
      }
    } catch (err) {
      console.error('Error resending email:', err);
      notify.error(err.message || 'Failed to resend email');
    } finally {
      setResending(null);
    }
  };

  const handleViewEmail = (email) => {
    setSelectedEmail(email);
    setViewDialogOpen(true);
  };

  const handleApplyFilters = () => {
    setPage(0);
    loadEmails();
  };

  const handleResetFilters = () => {
    setFilterStatus('all');
    setFilterType('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterRecipient('');
    setPage(0);
    loadEmails();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      case 'bounced':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return <SuccessIcon fontSize="small" />;
      case 'failed':
      case 'bounced':
        return <ErrorIcon fontSize="small" />;
      case 'pending':
        return <PendingIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const getEmailTypeName = (type) => {
    const types = {
      contract_created: 'Contract Created',
      contract_approval: 'Contract Pending Approval',
      contract_approved: 'Contract Approved',
      contract_rejected: 'Contract Rejected',
      payment_reminder: 'Payment Reminder',
      payment_confirmation: 'Payment Confirmation',
      contract_maturity: 'Contract Maturity Notice',
      rate_change: 'Rate Change Notification',
      system_alert: 'System Alert',
    };
    return types[type] || type;
  };

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Email Audit Trail
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Track all emails sent by the system, including delivery status and open rates
          </Typography>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandIcon />}>
              <Typography>Filters</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid
                  size={{
                    xs: 12,
                    md: 3
                  }}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="delivered">Delivered</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                      <MenuItem value="bounced">Bounced</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 3
                  }}>
                  <FormControl fullWidth>
                    <InputLabel>Email Type</InputLabel>
                    <Select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      label="Email Type"
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      <MenuItem value="contract_created">Contract Created</MenuItem>
                      <MenuItem value="contract_approval">Pending Approval</MenuItem>
                      <MenuItem value="contract_approved">Contract Approved</MenuItem>
                      <MenuItem value="payment_reminder">Payment Reminder</MenuItem>
                      <MenuItem value="payment_confirmation">Payment Confirmation</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 2
                  }}>
                  <TextField
                    fullWidth
                    label="Date From"
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 2
                  }}>
                  <TextField
                    fullWidth
                    label="Date To"
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 2
                  }}>
                  <TextField
                    fullWidth
                    label="Recipient Email"
                    value={filterRecipient}
                    onChange={(e) => setFilterRecipient(e.target.value)}
                    placeholder="email@example.com"
                  />
                </Grid>

                <Grid size={12}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={handleResetFilters}
                    >
                      Reset
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={handleApplyFilters}
                    >
                      Apply Filters
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
      <Paper>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Email History ({totalCount} total)
          </Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadEmails}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <SortableHeaderCell sort={sort} columnKey="created_at">Date Sent</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="recipient_email">Recipient</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="subject">Subject</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="notification_type">Type</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="status">Status</SortableHeaderCell>
                <TableCell>Opened</TableCell>
                <TableCell>Contract Ref</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {emails.map((email) => (
                <TableRow key={email.id} hover>
                  <TableCell>
                    {format(new Date(email.sent_at), 'dd MMM yyyy HH:mm')}
                  </TableCell>
                  <TableCell>{email.recipient}</TableCell>
                  <TableCell>{email.subject}</TableCell>
                  <TableCell>
                    <Chip
                      label={getEmailTypeName(email.type)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(email.status)}
                      label={email.status}
                      color={getStatusColor(email.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {email.opened_at ? (
                      <Typography variant="body2" color="success.main">
                        {format(new Date(email.opened_at), 'dd MMM HH:mm')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Not opened
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{email.contract_reference || email.reference_id || '-'}</TableCell>
                  <TableCell>
                    <Tooltip title="View email details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewEmail(email)}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {(email.status === 'failed' || email.status === 'bounced') && (
                      <Tooltip title="Resend email">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleResendEmail(email.id)}
                          disabled={resending === email.id}
                        >
                          <ResendIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
      {/* View Email Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Email Details
        </DialogTitle>
        <DialogContent>
          {selectedEmail && (
            <Box>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Typography variant="caption" color="textSecondary">Recipient</Typography>
                  <Typography variant="body1">{selectedEmail.recipient}</Typography>
                </Grid>

                <Grid size={12}>
                  <Typography variant="caption" color="textSecondary">Subject</Typography>
                  <Typography variant="body1">{selectedEmail.subject}</Typography>
                </Grid>

                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Email Type</Typography>
                  <Typography variant="body1">{getEmailTypeName(selectedEmail.type)}</Typography>
                </Grid>

                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Status</Typography>
                  <Chip
                    icon={getStatusIcon(selectedEmail.status)}
                    label={selectedEmail.status}
                    color={getStatusColor(selectedEmail.status)}
                    size="small"
                  />
                </Grid>

                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Sent At</Typography>
                  <Typography variant="body1">
                    {format(new Date(selectedEmail.sent_at), 'dd MMM yyyy HH:mm:ss')}
                  </Typography>
                </Grid>

                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Delivered At</Typography>
                  <Typography variant="body1">
                    {selectedEmail.delivered_at
                      ? format(new Date(selectedEmail.delivered_at), 'dd MMM yyyy HH:mm:ss')
                      : 'Not delivered'}
                  </Typography>
                </Grid>

                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Opened At</Typography>
                  <Typography variant="body1">
                    {selectedEmail.opened_at
                      ? format(new Date(selectedEmail.opened_at), 'dd MMM yyyy HH:mm:ss')
                      : 'Not opened'}
                  </Typography>
                </Grid>

                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Contract Reference</Typography>
                  <Typography variant="body1">{selectedEmail.contract_reference || 'N/A'}</Typography>
                </Grid>

                <Grid size={12}>
                  <Typography variant="caption" color="textSecondary">Email Body Preview</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      Dear User,

                      {selectedEmail.type === 'contract_approval' && `\n\nContract ${selectedEmail.contract_reference} has been submitted for your approval.\n\nPlease review and approve or reject the contract in the Capital Management System.`}
                      {selectedEmail.type === 'payment_reminder' && `\n\nThis is a reminder that a payment is due for contract ${selectedEmail.contract_reference}.\n\nPlease ensure payment is made by the due date.`}
                      {selectedEmail.type === 'contract_approved' && `\n\nContract ${selectedEmail.contract_reference} has been approved and is now active.`}

                      {'\n\nRegards,\nCapital Management System'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EmailAuditTrail;
